import type { RosterCharacter } from '../net/account';

/**
 * The character selector's arithmetic: what a roster can still do, and whether a name will
 * be accepted before it is sent.
 *
 * Pure and separate from the components for the reason `drag.ts` and `minimap.ts` are — the
 * part that can be *wrong* here does not throw. A name rule that is looser than the
 * server's produces a form that submits and fails; one that is tighter refuses names the
 * server would have taken, and the player has no way to find out which.
 */

/**
 * How long a name may be, mirrored from `store.ValidateCharacterName`.
 *
 * **Mirrored, and therefore walked by a test rather than trusted.** This is the same
 * hand-copied vocabulary problem as `slotsFor` and `WeaponClass`: the server is the
 * authority and this exists only so the player learns a name is too long *while typing it*
 * instead of after pressing create.
 */
export const NAME_MIN = 2;
export const NAME_MAX = 20;

/**
 * Why a name will be refused, in the player's words — or `null` when it will be accepted.
 *
 * `null` is deliberately not "valid": the server still decides, and it knows two things
 * this cannot — whether the name is already taken in that league, and whether the roster is
 * full. Those come back as errors and are shown the same way.
 */
export function nameProblem(raw: string): string | null {
  const name = raw.trim();
  if (name.length === 0) return 'A name is needed.';
  if (name.length < NAME_MIN) return `At least ${NAME_MIN} characters.`;
  if (name.length > NAME_MAX) return `At most ${NAME_MAX} characters.`;
  if (/\s\s/.test(name)) return 'No two spaces in a row.';
  // Control and format characters are refused explicitly by the server: a zero-width joiner
  // is invisible, so two names that draw identically would be two different characters.
  if (/[\p{C}]/u.test(name)) return 'No invisible characters.';
  if (!/^[\p{L}\p{N} '-]+$/u.test(name)) {
    return 'Letters, digits, spaces, hyphens and apostrophes only.';
  }
  return null;
}

/** Whether the create button should be pressable at all. */
export function canCreate(
  draft: { name: string; league: string; colour: string; archetype: string; armourSet: string },
  roster: { characters: unknown[]; max: number },
): boolean {
  if (roster.characters.length >= roster.max) return false;
  if (nameProblem(draft.name) !== null) return false;
  return (
    draft.league !== '' &&
    draft.colour !== '' &&
    draft.archetype !== '' &&
    draft.armourSet !== ''
  );
}

/** How many more characters this account may make. */
export function slotsLeft(roster: { characters: unknown[]; max: number }): number {
  return Math.max(0, roster.max - roster.characters.length);
}

/**
 * What a starting kit is called, and what it means.
 *
 * A `Record`, so TypeScript refuses an incomplete map: a fourth archetype added to the wire
 * without an entry here is a compile error rather than a blank card in the creator.
 */
export const ARCHETYPES: Record<string, { name: string; blurb: string }> = {
  SWORD: { name: 'Sword', blurb: 'A one-handed blade. Close, reliable, forgiving.' },
  BOW: { name: 'Bow', blurb: 'Reach. Strikes from further than anything can reach back.' },
  WAND: { name: 'Wand', blurb: 'A caster’s arm. Ranged, and scales on Intelligence.' },
};

export function archetypeName(id: string): string {
  return ARCHETYPES[id]?.name ?? id;
}

export function archetypeBlurb(id: string): string {
  return ARCHETYPES[id]?.blurb ?? '';
}

/**
 * What a starting armour set is called, and what it means.
 *
 * A `Record` for the same reason `ARCHETYPES` is one: a fourth set added to the wire
 * without an entry here is a compile error rather than a blank card in the creator.
 *
 * **The blurbs say what it does today, not what it sounds like.** Armour, evasion and
 * energy shield are all summed honestly onto the character and read by nothing in the
 * simulation (`doc/grind/design/CLASSES-ATTRIBUTES.md`) — so a card promising *tougher* would be a lie the
 * player could not check. What the choice really decides is the silhouette, and that is
 * what these say.
 */
export const ARMOUR_SETS: Record<string, { name: string; blurb: string }> = {
  CLOTH: { name: 'Cloth', blurb: 'Robes. Reads as a scholar, and carries an energy shield.' },
  LEATHER: { name: 'Leather', blurb: 'Padded hide. Reads as an adventurer, and evades.' },
  PLATE: { name: 'Plate', blurb: 'Iron. Reads as a knight, and carries the most armour.' },
};

export function armourSetName(id: string): string {
  return ARMOUR_SETS[id]?.name ?? id;
}

export function armourSetBlurb(id: string): string {
  return ARMOUR_SETS[id]?.blurb ?? '';
}

/**
 * When a character was last played, as a person would say it.
 *
 * Coarse on purpose: the selector uses this to answer *which one was I on*, and to that
 * question "3 days ago" and "3 days and 4 hours ago" are the same answer. Anything finer
 * implies a precision nobody acts on.
 */
export function playedAgo(playedAt: number, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.floor(now / 1000) - playedAt);
  if (seconds < 90) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return 'a long time ago';
}

/**
 * The roster in the order it is drawn: most recently played first.
 *
 * The server already sorts this way; sorting again here is not redundancy but independence
 * — a locally-created character is spliced into the list without a round trip, and it has
 * to land in the right place.
 */
export function inPlayOrder(characters: readonly RosterCharacter[]): RosterCharacter[] {
  return [...characters].sort((a, b) => b.playedAt - a.playedAt);
}
